import type { AdminRole } from '@/lib/api-admin/types';
import { adminFetch } from '@/lib/apiClient';
import type { ProductCollectionGroup, ProductCollectionInputs } from '../hooks/useProductCollectionInputs';

/**
 * 상품 추가입력 개인정보 원문 접근 — 원문보기·엑셀 내보내기(be-catalog feat/legacy-collection-encrypt, b662243).
 *
 * 추가입력의 개인정보 항목은 암호화 저장되고, 기본 조회는 "********" 로 가려 내려온다. 원문이 필요할 때 쓰는
 * 두 기능은 **호출할 때마다 BE 감사로그에 남는다**(PRODUCT_COLLECTION_RAW_VIEW / PRODUCT_COLLECTION_EXPORT, 2년 보관).
 * 그래서 BO 는
 *   - 버튼을 눌렀을 때만 호출하고(자동 호출·재시도 금지), 확인을 한 번 받는다.
 *   - 원문은 화면 상태에만 둔다 — react-query 캐시·localStorage 에 넣지 않고, 화면을 떠나면 버린다.
 */

/** 원문보기 권한 — BE @PreAuthorize 와 같다. 그 외 역할은 403 이라 버튼을 숨긴다. */
export const RAW_VIEW_ROLES: ReadonlyArray<AdminRole> = ['SUPER_ADMIN', 'OPS_ADMIN', 'CS_AGENT'];
/** 엑셀 내보내기 권한 — CS_AGENT 는 403. */
export const EXPORT_ROLES: ReadonlyArray<AdminRole> = ['SUPER_ADMIN', 'OPS_ADMIN'];

export const hasAnyRole = (roles: ReadonlyArray<AdminRole>, allowed: ReadonlyArray<AdminRole>): boolean =>
  roles.some((r) => allowed.includes(r));

/** 실패 응답(JSON envelope)에서 운영자에게 보여 줄 문구를 꺼낸다. 필드별 사유(details)가 있으면 이어 붙인다. */
export const readErrorMessage = async (res: Response, fallback: string): Promise<string> => {
  try {
    const text = await res.text();
    const env = JSON.parse(text) as {
      error?: { message?: string; details?: unknown } | null;
      message?: string;
    };
    const message = env.error?.message ?? env.message ?? '';
    const details = env.error?.details;
    const detailText = Array.isArray(details)
      ? details
          .map((d) =>
            typeof d === 'string'
              ? d
              : d && typeof d === 'object'
                ? String((d as { message?: unknown; reason?: unknown }).message ?? (d as { reason?: unknown }).reason ?? '')
                : '',
          )
          .filter(Boolean)
          .join(' / ')
      : details && typeof details === 'object'
        ? Object.values(details as Record<string, unknown>).map(String).join(' / ')
        : '';
    const combined = [message, detailText].filter(Boolean).join(' — ');
    return combined || fallback;
  } catch {
    return fallback;
  }
};

/**
 * 원문 조회. 응답 구조는 기본 조회와 같고 masked 가 모두 false 다(복호화 실패 항목은 value "(복호화 실패)").
 * 캐시에 남지 않도록 react-query 를 거치지 않고 직접 호출한다.
 */
export const fetchRawCollectionInputs = async (orderId: string): Promise<ProductCollectionInputs> => {
  const res = await adminFetch(`/admin/v1/product-collections/by-order/${orderId}/raw`, { cache: 'no-store' });
  if (!res.ok) {
    const fallback =
      res.status === 403 ? '원문을 볼 권한이 없습니다.' : '원문을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.';
    throw new Error(await readErrorMessage(res, fallback));
  }
  const env = (await res.json()) as {
    data?: { orderId?: string; products?: ProductCollectionGroup[] | null } | null;
  };
  return {
    orderId: env.data?.orderId ?? orderId,
    products: (env.data?.products ?? []).filter((g): g is ProductCollectionGroup => g != null),
  };
};

// ───────────────────────── 엑셀 내보내기 ─────────────────────────

export interface CollectionExportForm {
  productId: string | null;
  /** yyyy-MM-dd (주문일 KST). 빈 문자열 = 미지정. */
  from: string;
  to: string;
  reason: string;
  password: string;
}

/** BE ProductCollectionRawAccessService 와 같은 한도. */
export const EXPORT_MAX_PERIOD_DAYS = 31;
export const EXPORT_REASON_MAX = 200;
export const EXPORT_PASSWORD_MIN = 8;
export const EXPORT_PASSWORD_MAX = 64;

export type CollectionExportProblem =
  | 'reasonRequired'
  | 'reasonTooLong'
  | 'scopeRequired'
  | 'periodPair'
  | 'periodOrder'
  | 'periodTooLong'
  | 'passwordLength';

const dayIndex = (ymd: string): number => {
  const [y, m, d] = ymd.split('-').map(Number);
  return Date.UTC(y ?? 0, (m ?? 1) - 1, d ?? 1) / 86_400_000;
};

/** 내보내기 조건 검사 — BE 400 을 미리 막는다. 걸리는 첫 항목을 돌려준다. */
export const validateCollectionExport = (form: CollectionExportForm): CollectionExportProblem | null => {
  const reason = form.reason.trim();
  if (reason === '') return 'reasonRequired';
  if (reason.length > EXPORT_REASON_MAX) return 'reasonTooLong';
  const hasFrom = form.from !== '';
  const hasTo = form.to !== '';
  if (hasFrom !== hasTo) return 'periodPair';
  if (!form.productId && !hasFrom) return 'scopeRequired';
  if (hasFrom && hasTo) {
    const days = dayIndex(form.to) - dayIndex(form.from) + 1; // 양끝 포함
    if (days < 1) return 'periodOrder';
    if (!form.productId && days > EXPORT_MAX_PERIOD_DAYS) return 'periodTooLong';
  }
  const pw = form.password;
  if (pw !== '' && (pw.length < EXPORT_PASSWORD_MIN || pw.length > EXPORT_PASSWORD_MAX)) return 'passwordLength';
  return null;
};

/**
 * 요청 본문. 빈 값은 키째 뺀다 — 특히 password 는 BE 가 @Size(min = 8) 라 "" 를 보내면 400 이다.
 */
export const buildCollectionExportBody = (form: CollectionExportForm): Record<string, string> => ({
  ...(form.productId ? { productId: form.productId } : {}),
  ...(form.from ? { from: form.from } : {}),
  ...(form.to ? { to: form.to } : {}),
  reason: form.reason.trim(),
  ...(form.password ? { password: form.password } : {}),
});

export const EXPORT_FILENAME = 'product-collection-inputs.xlsx';

/**
 * 내보내기 호출 → xlsx 다운로드. 사유·비밀번호가 URL·접근로그에 남지 않도록 POST body 로 보낸다.
 * 실패는 JSON envelope 이라 본문을 텍스트로 읽어 BE 메시지를 그대로 던진다(5000행 초과·조건 오류 등).
 */
export const downloadCollectionExport = async (form: CollectionExportForm): Promise<void> => {
  const res = await adminFetch('/admin/v1/product-collections/export', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(buildCollectionExportBody(form)),
    cache: 'no-store',
  });
  if (!res.ok) {
    const fallback =
      res.status === 403 ? '엑셀을 내려받을 권한이 없습니다.' : '엑셀을 만들지 못했습니다. 잠시 후 다시 시도해 주세요.';
    throw new Error(await readErrorMessage(res, fallback));
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = EXPORT_FILENAME;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
